import { useMemo } from 'react';

import { PostFilter } from '../../../../entities/post/models';
import { useFilteredPostsNavigation } from '../../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../../filtered-profiles/hook/use-filtered-profiles-navigation';
import { getThemeTableColumns } from '../configs/theme.table.columns';
import { showDrillDownContextMenu } from '../overlay/show-drill-down-context-menu';
import type { ThemeDetail, VolumeType } from '../types';

import { PageNavigation } from '@/shared/components/page-navigation/page-navigation';
import { StaticTable } from '@/shared/components/static-table/static-table';
import { useStaticTable } from '@/shared/components/static-table/use-static-table';

interface Props {
  data: ThemeDetail[];
  volumeType: VolumeType;
  sentiment: 'positive' | 'negative';
  parentDimension?: 'brand' | 'product'; // Added to track the original drill down context
  parentFilter?: Record<string, string>; // Added to include brand/product filters
  isLoading: boolean;
}

const PAGE_SIZE = 10;

export function ThemeTable({
  data,
  volumeType,
  sentiment,
  parentDimension,
  parentFilter,
  isLoading,
}: Props) {
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();

  const handleRowClick = async (theme: ThemeDetail, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
    });

    // Build filter with theme and sentiment, plus any parent filters
    const filter = {
      ...parentFilter, // Include brand/product filters if coming from drill down
      [PostFilter.Theme]: theme.name,
      [PostFilter.Sentiment]: sentiment,
    };

    // Use parent dimension if this came from brand/product drill down, otherwise use theme
    const dimension =
      parentDimension === 'brand'
        ? PostFilter.Brand
        : parentDimension === 'product'
          ? PostFilter.Product
          : PostFilter.Theme;

    switch (action) {
      case 'view-posts':
        await navigateToFilteredPosts({
          filter,
          dimension,
        });
        break;
      case 'view-profiles':
        await navigateToFilteredProfiles({
          filter,
          dimension,
        });
        break;
      case 'close':
        break;
    }
  };

  // Get columns configuration
  const columns = useMemo(() => {
    return getThemeTableColumns(sentiment, volumeType);
  }, [sentiment, volumeType]);

  // Setup table with pagination
  const { table, gridTemplateColumns } = useStaticTable({
    data: data || [],
    columns,
    pageSize: PAGE_SIZE,
    showIndexColumn: false, // We provide our own index column in the column configs
  });

  return (
    <>
      <div className="px-6 pb-6">
        <StaticTable
          isLoading={isLoading}
          pageSize={PAGE_SIZE}
          table={table}
          gridTemplateColumns={gridTemplateColumns}
          onRowClick={handleRowClick}
        />
      </div>
      {!isLoading && <PageNavigation className="border-t" table={table} />}
    </>
  );
}
